import { createHash } from "node:crypto";

import { AuthenticationError, ConfigurationError, NetworkError } from "../../../errors.js";
import { mapOpenAICompatibleError } from "../../error-mappers/openai-compatible.js";
import { mapWithConcurrency } from "../../runtime/concurrency/map-with-concurrency.js";
import type {
  CreateAdapterOptions,
  EmbeddingRuntime,
  EmbeddingRuntimeStats,
} from "../embedding-adapter.js";
import { globalEmbeddingCache } from "../embedding-cache.js";

/**
 * Shared factory for OpenAI-compatible embedding providers (OpenAI, Mistral,
 * DeepInfra, and anything else exposing `POST /v1/embeddings` with the
 * `{ model, input }` request shape and `{ data: [{ embedding }] }` response).
 *
 * Mirrors the inner mechanics of OpenClaw's batch embedding runtime.
 *
 * @internal
 */

const MAX_BATCH = 100;
const MAX_RETRIES = 2;

export interface OpenAiCompatibleConfig {
  id: string;
  defaultBaseUrl: string;
  apiKeyEnv: string;
  baseUrlEnv?: string;
  defaultModel: string;
  /**
   * Dimension hint by model id. The chosen model MUST be in this table —
   * unknown models throw `ConfigurationError(code: "embedding_unknown_model")`
   * (EC-4 fix). This prevents vec0 virtual-table dimension mismatches that
   * surface as cryptic errors downstream.
   */
  dimensionByModel: Record<string, number>;
  /**
   * Path component appended to `baseUrl` for the embeddings endpoint.
   * Default `"/v1/embeddings"` (OpenAI canonical). REPLACES the default —
   * does NOT concatenate. Used by DeepInfra (`"/v1/openai/embeddings"`),
   * etc. (EC-2 fix).
   */
  embeddingsPath?: string;
}

export async function createOpenAiCompatibleRuntime(
  cfg: OpenAiCompatibleConfig,
  options: CreateAdapterOptions,
): Promise<EmbeddingRuntime> {
  const model = options.model ?? cfg.defaultModel;
  const apiKey = options.apiKey ?? process.env[cfg.apiKeyEnv];
  if (apiKey === undefined || apiKey.length === 0) {
    throw new AuthenticationError(`${cfg.apiKeyEnv} missing`, {
      code: "embedding_missing_api_key",
    });
  }
  const envBaseUrl = cfg.baseUrlEnv !== undefined ? process.env[cfg.baseUrlEnv] : undefined;
  const baseUrl = options.baseUrl ?? envBaseUrl ?? cfg.defaultBaseUrl;
  const fetchImpl = options.fetch ?? fetch;
  // T4.4 — default to process-wide singleton (was per-adapter instance).
  const cache = options.cache ?? globalEmbeddingCache;
  // EC-4: refuse unknown models to prevent vec0 dimension mismatches downstream.
  const dimension = cfg.dimensionByModel[model];
  if (dimension === undefined) {
    throw new ConfigurationError(
      `${cfg.id} adapter: model "${model}" is not in the dimension table. Use one of: ${Object.keys(cfg.dimensionByModel).join(", ")}. To add it, update dimensionByModel in the adapter source.`,
      { code: "embedding_unknown_model" },
    );
  }
  const embeddingsPath = cfg.embeddingsPath ?? "/v1/embeddings";

  const stats: EmbeddingRuntimeStats = {
    cacheHits: 0,
    cacheMisses: 0,
    httpCalls: 0,
    retries: 0,
  };

  return {
    id: cfg.id,
    model,
    dimension,
    stats: () => ({ ...stats }),
    embed: (texts) =>
      embedTexts({
        texts,
        cache,
        stats,
        model,
        dimension,
        apiKey,
        baseUrl,
        embeddingsPath,
        fetchImpl,
        providerId: cfg.id,
      }),
  };
}

interface EmbedTextsInput {
  texts: ReadonlyArray<string>;
  cache: NonNullable<CreateAdapterOptions["cache"]>;
  stats: EmbeddingRuntimeStats;
  model: string;
  dimension: number;
  apiKey: string;
  baseUrl: string;
  embeddingsPath: string;
  fetchImpl: typeof fetch;
  providerId: string;
}

async function embedTexts(input: EmbedTextsInput): Promise<number[][]> {
  const { texts, cache, stats, model, dimension } = input;
  const results = new Array<number[] | undefined>(texts.length);
  const pending: Array<{ index: number; text: string; key: string }> = [];
  for (let i = 0; i < texts.length; i++) {
    classifyEntry({
      index: i,
      text: texts[i] ?? "",
      model,
      dimension,
      cache,
      stats,
      results,
      pending,
    });
  }
  await embedInBoundedBatches(input, pending, results);
  return results.map((v) => v ?? new Array(dimension).fill(0));
}

interface ClassifyEntryArgs {
  index: number;
  text: string;
  model: string;
  dimension: number;
  cache: NonNullable<CreateAdapterOptions["cache"]>;
  stats: EmbeddingRuntimeStats;
  results: Array<number[] | undefined>;
  pending: Array<{ index: number; text: string; key: string }>;
}

function classifyEntry(args: ClassifyEntryArgs): void {
  if (args.text.trim().length === 0) {
    args.results[args.index] = new Array(args.dimension).fill(0);
    return;
  }
  const key = hashKey(args.model, args.text);
  const cached = args.cache.get(key);
  if (cached !== undefined) {
    args.stats.cacheHits += 1;
    args.results[args.index] = cached;
    return;
  }
  args.stats.cacheMisses += 1;
  args.pending.push({ index: args.index, text: args.text, key });
}

// T4.3 — max concurrent embedding API requests per embed() call.
// 3 is enough to saturate most embedding APIs without triggering
// rate limits (each batch is already up to MAX_BATCH=100 texts).
const MAX_CONCURRENT_BATCHES = 3;

/**
 * T4.3 — parallel embed batches (DR4 finding #3).
 *
 * Pre-T4.3 this was a serial `for` loop — each batch of 100 texts
 * awaited before the next HTTP request started. With 500 texts the
 * serial path took 5 × RTT; parallel takes max(RTT) × ceil(5/3).
 *
 * Uses bounded-concurrency `Promise.all` capped at 3 concurrent
 * HTTP requests to avoid overwhelming the embedding API.
 */
async function embedInBoundedBatches(
  input: EmbedTextsInput,
  pending: ReadonlyArray<{ index: number; text: string; key: string }>,
  results: Array<number[] | undefined>,
): Promise<void> {
  const batches: Array<ReadonlyArray<{ index: number; text: string; key: string }>> = [];
  for (let offset = 0; offset < pending.length; offset += MAX_BATCH) {
    batches.push(pending.slice(offset, offset + MAX_BATCH));
  }
  // M0-2: bounded parallel via the shared ordered pool (was an inline
  // acquire/release clone — see plan m0-foundation-expose-primitives).
  await mapWithConcurrency(batches, MAX_CONCURRENT_BATCHES, (batch) =>
    processBatch(input, batch, results),
  );
}

async function processBatch(
  input: EmbedTextsInput,
  batch: ReadonlyArray<{ index: number; text: string; key: string }>,
  results: Array<number[] | undefined>,
): Promise<void> {
  const vectors = await embedBatch({
    apiKey: input.apiKey,
    baseUrl: input.baseUrl,
    embeddingsPath: input.embeddingsPath,
    model: input.model,
    inputs: batch.map((b) => b.text),
    fetchImpl: input.fetchImpl,
    stats: input.stats,
    providerId: input.providerId,
  });
  for (let j = 0; j < batch.length; j++) {
    const slot = batch[j];
    const vector = vectors[j];
    if (slot === undefined || vector === undefined) continue;
    results[slot.index] = vector;
    input.cache.set(slot.key, vector);
  }
}

interface BatchOptions {
  apiKey: string;
  baseUrl: string;
  embeddingsPath: string;
  model: string;
  inputs: ReadonlyArray<string>;
  fetchImpl: typeof fetch;
  stats: EmbeddingRuntimeStats;
  providerId: string;
}

async function embedBatch(opts: BatchOptions): Promise<number[][]> {
  // EC-2: embeddingsPath REPLACES the suffix; never concatenates.
  const url = `${opts.baseUrl.replace(/\/$/, "")}${opts.embeddingsPath}`;
  let attempt = 0;
  while (true) {
    opts.stats.httpCalls += 1;
    const response = await postEmbedRequest(opts, url);
    if (response.ok)
      return await parseEmbedResponse(response, opts.providerId, opts.embeddingsPath);
    if (isRetryable(response.status) && attempt < MAX_RETRIES) {
      attempt += 1;
      opts.stats.retries += 1;
      await sleep(linearBackoffMs(attempt));
      continue;
    }
    // Read body (best-effort) so the mapper has access to provider error code.
    const text = await response.text().catch(() => "");
    let body: unknown = text;
    try {
      body = JSON.parse(text);
    } catch {
      // not JSON — keep as string
    }
    throw mapOpenAICompatibleError({
      providerId: opts.providerId,
      status: response.status,
      body,
      headers: response.headers,
      endpoint: opts.embeddingsPath,
    });
  }
}

async function postEmbedRequest(opts: BatchOptions, url: string): Promise<Response> {
  return opts.fetchImpl(url, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${opts.apiKey}`,
    },
    body: JSON.stringify({ model: opts.model, input: opts.inputs }),
  });
}

async function parseEmbedResponse(
  response: Response,
  providerId: string,
  endpoint: string,
): Promise<number[][]> {
  const json = (await response.json()) as { data?: Array<{ embedding: number[] }> };
  if (!Array.isArray(json.data)) {
    throw new NetworkError(`${providerId} ${endpoint} returned no data`, {
      code: "embedding_invalid_response",
      metadata: {
        provider: providerId,
        endpoint,
        code: "invalid_request",
        raw: json,
      },
    });
  }
  return json.data.map((d) => d.embedding);
}

function isRetryable(status: number): boolean {
  return status === 429 || (status >= 500 && status < 600);
}

function hashKey(model: string, text: string): string {
  return createHash("sha256").update(`${model} ${text}`).digest("hex");
}

function linearBackoffMs(attempt: number): number {
  return 50 * attempt;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
